/**
 * Redux Reducer for Image Modal Upload State
 *
 * Gerencia o estado completo do modal de upload de imagens
 *
 * ESTRUTURA DO ESTADO (state.image_modal.modal):
 * {
 *   isOpen: boolean,        // Modal está visível (não usado, controlado pelo parent)
 *   isUploading: boolean,   // Upload em progresso?
 *   error: string|null,     // Mensagem de erro (null = sem erro)
 *   uploadedImage: object|null // Dados da imagem criada (null = nenhuma imagem)
 * }
 *
 * TRANSIÇÕES DE ESTADO:
 *
 * IDLE → REQUEST:
 *   isUploading: false → true
 *   error: null (reseta)
 *   uploadedImage: unchanged
 *   Quando: Usuário clica "Enviar"
 *
 * REQUEST → SUCCESS:
 *   isUploading: true → false
 *   error: null
 *   uploadedImage: null → {imagem com @id}
 *   Quando: uploadContent completa com sucesso
 *
 * REQUEST → FAILURE:
 *   isUploading: true → false
 *   error: null → "mensagem"
 *   uploadedImage: null (limpa)
 *   Quando: uploadContent falha
 *
 * SUCCESS/FAILURE/OPEN → CLOSE:
 *   isUploading: false (não muda)
 *   error: (qualquer) → null (limpa)
 *   uploadedImage: (qualquer) → null (limpa)
 *   Quando: Modal fecha ou usuário cancela
 *
 * DIAGRAMA DE ESTADO:
 *
 *  ┌─────────┐
 *  │  IDLE   │  (isUploading=false, uploadedImage=null, error=null)
 *  └────┬────┘
 *       │ REQUEST (usuário clica "Enviar")
 *       ↓
 *  ┌──────────────┐
 *  │  UPLOADING   │  (isUploading=true)
 *  └────┬─────┬──┘
 *       │     │
 *  SUCCESS  FAILURE
 *       │     │
 *       ↓     ↓
 *  ┌──────────────┐
 *  │    ERROR     │  (isUploading=false, error="msg")
 *  │   or SUCCESS │  (isUploading=false, uploadedImage={...})
 *  └────┬──────┬──┘
 *       │      │
 *     CLOSE  CLOSE
 *       │      │
 *       └──────┘
 *            ↓
 *       ┌─────────┐
 *       │  IDLE   │  (volta ao estado inicial)
 *       └─────────┘
 */

import {
  CLOSE_IMAGE_MODAL,
  UPLOAD_IMAGE_WITH_METADATA_FAILURE,
  UPLOAD_IMAGE_WITH_METADATA_REQUEST,
  UPLOAD_IMAGE_WITH_METADATA_SUCCESS,
} from 'govbr.frontend.policy/constants/ActionTypes';

const initialState = {
  modal: {
    isOpen: false,
    isUploading: false,
    error: null,
    uploadedImage: null,
  },
};

/**
 * Image Modal Reducer
 *
 * @param {Object} state - Estado atual (default: initialState)
 * @param {Object} action - Action Redux { type, payload?, error? }
 * @returns {Object} Novo estado
 */
export default function image_modal(state = initialState, action) {
  switch (action.type) {
    // ============ REQUEST ============
    // Usuário clicou "Enviar" - upload iniciando
    case UPLOAD_IMAGE_WITH_METADATA_REQUEST:
      return {
        ...state,
        modal: {
          ...state.modal,
          isUploading: true, // ← Ativar indicador de upload
          error: null, // ← Limpar erro anterior
          // uploadedImage: não muda (pode estar vindo de upload anterior)
        },
      };

    // ============ SUCCESS ============
    // uploadContent completou com sucesso
    case UPLOAD_IMAGE_WITH_METADATA_SUCCESS:
      return {
        ...state,
        modal: {
          ...state.modal,
          isUploading: false, // ← Desativar indicador
          uploadedImage: action.payload, // ← Salvar dados da imagem (com @id)
          error: null, // ← Garantir erro zerado
        },
      };

    // ============ FAILURE ============
    // uploadContent falhou ou erro na leitura do arquivo
    case UPLOAD_IMAGE_WITH_METADATA_FAILURE:
      return {
        ...state,
        modal: {
          ...state.modal,
          isUploading: false, // ← Desativar indicador
          error: action.error, // ← Salvar mensagem de erro
          uploadedImage: null, // ← Limpar dados parciais
        },
      };

    // ============ CLOSE ============
    // Modal fechou (sucesso, erro, ou cancelamento)
    // CRÍTICO: Zerar uploadedImage para permitir novo upload
    case CLOSE_IMAGE_MODAL:
      return {
        ...state,
        modal: {
          ...state.modal,
          isOpen: false, // ← Fechar modal (se estivesse aberto)
          error: null, // ← Limpar erro (permite retry)
          uploadedImage: null, // ← CRÍTICO: Zerar para permitir novo upload
          isUploading: false, // ← Garantir não estar uploadando
        },
      };

    // ============ DEFAULT ============
    default:
      return state;
  }
}

/**
 * SELECTORS (para usar no componente)
 *
 * import { useSelector } from 'react-redux';
 *
 * const isUploading = useSelector(state => state.image_modal?.modal?.isUploading);
 * const uploadedImage = useSelector(state => state.image_modal?.modal?.uploadedImage);
 * const error = useSelector(state => state.image_modal?.modal?.error);
 *
 * // Ou criar selector customizado:
 * const selectImageModalState = (state) => state.image_modal?.modal || initialState.modal;
 */
